import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup as QomponentsButtonGroup } from '@seeqdev/qomponents';
import type { JSX } from 'react';

export interface ButtonItem<T extends string = string> {
  label: string;
  id: string;
  value: T;
  disabled?: boolean;
  tooltip?: string;
  icon?: string;
}

/**
 * An input component with a group of buttons corresponding to selectable options
 */
export interface ButtonGroupProps<T extends string = string> {
  buttonItems: ButtonItem<T>[];
  name: string;
  value: T;
  onChange: (value: T) => void;
  testId?: string;
}

export const ButtonGroup = <T extends string = string>({
  buttonItems,
  name,
  value,
  onChange,
  testId,
}: ButtonGroupProps<T>): JSX.Element => {
  const { t } = useTranslation();
  const selected = useMemo(() => value || buttonItems[0]?.value, [value, buttonItems]);

  const buttons = useMemo(
    () =>
      buttonItems.map((buttonItem) => ({
        variant: 'button' as const,
        buttonProps: {
          ...(buttonItem.id && { id: buttonItem.id }),
          ...(buttonItem.label && { label: t(buttonItem.label) }),
          ...(buttonItem.icon && { icon: buttonItem.icon }),
          value: buttonItem.value,
          isActive: buttonItem.value === selected,
          ...(buttonItem.disabled !== undefined && { disabled: buttonItem.disabled }),
          ...(buttonItem.tooltip && { tooltip: buttonItem.tooltip }),
          variant: 'outline' as const,
          size: 'xs' as const,
          extraClassNames: 'min-width-70',
        },
      })),
    [buttonItems, selected, t],
  );

  return (
    <QomponentsButtonGroup
      buttons={buttons}
      onChange={(newValue) => onChange(newValue as T)}
      testId={testId}
      extraClassNames="mt5"
    />
  );
};
